/**
 * Auth feature API service.
 *
 * Owns all auth-related HTTP calls except the bare cookie refresh
 * (`api/refresh-client.ts`), which has special interceptor-avoidance
 * concerns. Per `FRONTEND_DATA_STATE.md`, feature-specific API calls
 * live in the feature.
 *
 * Response-shape policy (Slice 2.6a contract — see
 * `@invento/shared/contracts/auth.contract.ts`):
 *   - `login` / `register` parse `{ user, tokens: { accessToken } }` and
 *     return `{ user, accessToken }`. `tokens.refreshToken` is gone from
 *     the backend response and from this envelope type.
 *   - `me` returns the canonical `AuthUser` shape used by the store.
 *   - `logout` returns void; the caller is responsible for clearing
 *     state and the query cache.
 *
 * Phase 4.4b-2:
 *   - `login` body switched to `{ identifier, password }`. The backend
 *     accepts the legacy `{ phoneNumber }` shape for one release; the
 *     dashboard moves forward only.
 *   - 8 new endpoints added for verification (4.2) + password reset
 *     (4.3). Email verification is a 6-digit OTP (4.4b-0); there is
 *     NO `/verify-email?token=` flow.
 */

import { apiClient } from '@/api/client';
import type { User } from '@/state/stores/auth.store';

export interface LoginInput {
  identifier: string;
  password: string;
}

export interface RegisterInput {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
  password: string;
  confirmPassword: string;
  invitationToken?: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
}

export interface AuthResult {
  user: User;
  accessToken: string;
}

interface AuthEnvelope {
  data: {
    user: User;
    tokens: { accessToken: string };
  };
}

interface MeEnvelope {
  data: User;
}

// Phase 4.4b-2 — verification + reset endpoint inputs.
export interface ConfirmEmailVerificationInput {
  otp: string;
}

export interface ConfirmPhoneVerificationInput {
  otp: string;
}

export interface RequestPasswordResetEmailInput {
  email: string;
}

export interface ConfirmPasswordResetEmailInput {
  token: string;
  newPassword: string;
}

export interface RequestPasswordResetSmsInput {
  phoneNumber: string;
}

export interface ConfirmPasswordResetSmsInput {
  phoneNumber: string;
  otp: string;
  newPassword: string;
}

export const authService = {
  async login(input: LoginInput): Promise<AuthResult> {
    const response = await apiClient.post<AuthEnvelope>('/auth/login', input);
    return {
      user: response.data.data.user,
      accessToken: response.data.data.tokens.accessToken,
    };
  },

  async register(input: RegisterInput): Promise<AuthResult> {
    const response = await apiClient.post<AuthEnvelope>('/auth/register', input);
    return {
      user: response.data.data.user,
      accessToken: response.data.data.tokens.accessToken,
    };
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  async me(): Promise<User> {
    const response = await apiClient.get<MeEnvelope>('/auth/me');
    return response.data.data;
  },

  // ─── Phase 4.4b-2 — verification (authenticated) ──────────────

  async requestEmailVerification(): Promise<void> {
    // Body intentionally empty: the backend reads the email off the
    // authenticated user record. The user MUST have an email on file
    // (server returns EMAIL_NOT_SET otherwise; UI handles).
    await apiClient.post('/auth/verify/email/request', {});
  },

  async confirmEmailVerification(input: ConfirmEmailVerificationInput): Promise<void> {
    await apiClient.post('/auth/verify/email/confirm', input);
  },

  async requestPhoneVerification(): Promise<void> {
    await apiClient.post('/auth/verify/phone/request', {});
  },

  async confirmPhoneVerification(input: ConfirmPhoneVerificationInput): Promise<void> {
    await apiClient.post('/auth/verify/phone/confirm', input);
  },

  // ─── Phase 4.4b-2 — password reset (pre-login) ────────────────

  async requestPasswordResetEmail(input: RequestPasswordResetEmailInput): Promise<void> {
    // Backend always returns ok({}) regardless of whether the email
    // resolves to an account — enumeration safety lives at the API
    // layer, not here.
    await apiClient.post('/auth/password-reset/email/request', input);
  },

  async confirmPasswordResetEmail(input: ConfirmPasswordResetEmailInput): Promise<void> {
    await apiClient.post('/auth/password-reset/email/confirm', input);
  },

  async requestPasswordResetSms(input: RequestPasswordResetSmsInput): Promise<void> {
    await apiClient.post('/auth/password-reset/sms/request', input);
  },

  async confirmPasswordResetSms(input: ConfirmPasswordResetSmsInput): Promise<void> {
    await apiClient.post('/auth/password-reset/sms/confirm', input);
  },
};
